import {
    ProjectCertainty,
    ProjectLifecycle,
    ProjectStatus,
    ProjectVisibility,
    ResourceAllocationState,
    Tag
} from '../../types';
import {getDescendantIds} from '../../lib/tagUtils';

/**
 * The What-if lens — filters a ResourceAllocationState down to the projects the
 * lens includes, cascading to everything the calc engine reads.
 *
 * PLEASE KEEP IN SYNC with the server's ScenarioProjection.cs (same flag
 * semantics, same cascade, mirrored unit-test case names).
 *
 * The default (ACTUALS) is the published picture: Published + Disclosed +
 * Confirmed projects only. Drafts are unpublished work-in-preparation
 * (possibly fully confirmed); tentative projects may not proceed — the lens
 * exists so planners can see their impact on demand without polluting the
 * default numbers.
 *
 * Two mutually exclusive modes:
 * - TOGGLE mode (scenarioTagIds empty): the include* flags each widen the
 *   published picture by a whole category.
 * - SCENARIO mode (scenarioTagIds non-empty): the flags are IGNORED. The
 *   result is exactly the published picture PLUS every project carrying a
 *   selected tag or a descendant of one — a named what-if ("Bid A").
 *
 * Exclusions apply in BOTH modes, and apply LAST: an excluded project is
 * dropped even if published or scenario-tagged. That is the cancellation
 * what-if ("what if we cancel X?") — combine with the additive side ("…and
 * we win tentative Y") in one lens.
 *
 * Lifecycle is special: a held/cancelled project with a lifecycleFromWeek is
 * TRUNCATED, not dropped — allocations before that week stay (the time was
 * really spent), allocations/phases from it drop, assignments with no
 * surviving hours drop (incl. open-role placeholders), and a project with
 * nothing spent before the cut vanishes whole. Null fromWeek = the original
 * binary behaviour. The include-on-hold/include-cancelled toggles restore the
 * FULL plan.
 *
 * Pure: never mutates the input; returns the SAME reference when nothing
 * would be dropped (load-bearing for memo identity in useAllocationLookup).
 */
export interface WhatIfLens {
    /** Include Draft (unpublished, in-preparation) projects. */
    includeDraft: boolean;
    /** Include NonDisclosed projects (only ones the viewer can already see). */
    includeNonDisclosed: boolean;
    /** Include Tentative projects (work that may not proceed). */
    includeTentative: boolean;
    /**
     * Include projects on hold (funding freeze, re-evaluation). Optional so
     * lenses stored before lifecycle existed keep working.
     */
    includeOnHold?: boolean;
    /** Include cancelled projects — the retrospective view. Optional as above. */
    includeCancelled?: boolean;
    /**
     * Scenario mode: project-tag ids acting as a named scenario. When
     * non-empty the include* flags are ignored — the numbers become the
     * published picture plus the tagged projects (child tags count).
     * Optional so lenses stored before this field existed keep working.
     */
    scenarioTagIds?: string[];
    /**
     * Cancellation what-if: project ids dropped from the picture. Applied
     * LAST, in both modes — exclusion beats every inclusion rule. Optional so
     * lenses stored before this field existed keep working.
     */
    excludedProjectIds?: string[];
}

/** The published picture — the default lens. */
export const ACTUALS: WhatIfLens = {
    includeDraft: false,
    includeNonDisclosed: false,
    includeTentative: false,
    includeOnHold: false,
    includeCancelled: false,
    scenarioTagIds: [],
    excludedProjectIds: []
};

/**
 * Selected scenario tag ids plus all their descendants — the full set of tag
 * ids that admit a project into a scenario. Shared with the banner so the
 * "N tagged projects" count matches the projection exactly.
 */
export const expandScenarioTagIds = (
    tagIds: string[],
    tags: Record<string, Tag>
): Set<string> => {
    const allTags = Object.values(tags);
    const expanded = new Set<string>(tagIds);
    for (const tagId of tagIds) {
        for (const descendantId of getDescendantIds(tagId, allTags)) {
            expanded.add(descendantId);
        }
    }
    return expanded;
};

const omitWhere = <T>(
    map: Record<string, T>,
    shouldRemove: (value: T) => boolean
): Record<string, T> =>
    Object.fromEntries(
        Object.entries(map).filter(([, value]) => !shouldRemove(value))
    );

export function applyScenarioToState(
    state: ResourceAllocationState,
    lens: WhatIfLens
): ResourceAllocationState {
    const scenarioTagIds = lens.scenarioTagIds ?? [];
    const scenarioMode = scenarioTagIds.length > 0;
    const excludedProjectIds = new Set(lens.excludedProjectIds ?? []);
    const includeOnHold = lens.includeOnHold ?? false;
    const includeCancelled = lens.includeCancelled ?? false;

    // Identity: the all-inclusive toggle lens with nothing excluded is a
    // no-op. (Scenario mode ignores the toggles, so it never takes this
    // fast path.)
    if (
        !scenarioMode &&
        excludedProjectIds.size === 0 &&
        lens.includeDraft &&
        lens.includeNonDisclosed &&
        lens.includeTentative &&
        includeOnHold &&
        includeCancelled
    ) {
        return state;
    }

    // KEEP IN SYNC with ProjectRules.IsActual (server) — the ACTUALS rule is
    // isActualIgnoringLifecycle AND an Active lifecycle. Split here because
    // lifecycle is the one category that truncates rather than drops.
    const isActualIgnoringLifecycle = (
        project: ResourceAllocationState['projects'][string]
    ) =>
        project.status === ProjectStatus.Published &&
        project.visibility === ProjectVisibility.Disclosed &&
        project.certainty === ProjectCertainty.Confirmed;
    const isLifecycleActive = (
        project: ResourceAllocationState['projects'][string]
    ) =>
        (project.lifecycle ?? ProjectLifecycle.Active) ===
        ProjectLifecycle.Active;

    // Scenario mode: published picture + projects carrying a selected tag
    // (or a descendant of one), regardless of why they were excluded.
    const scenarioTagSet = scenarioMode
        ? expandScenarioTagIds(scenarioTagIds, state.tags)
        : null;
    const scenarioProjectIds = scenarioTagSet
        ? new Set(
              Object.values(state.projectTags)
                  .filter(pt => scenarioTagSet.has(pt.tagId))
                  .map(pt => pt.projectId)
          )
        : null;

    // Non-lifecycle clauses of the toggle-mode rule — split out so an
    // otherwise-included held/cancelled project can be TRUNCATED at its
    // lifecycleFromWeek instead of dropped whole (effective-dated
    // cancellation: the time before that week was really spent).
    const passesNonLifecycle = (
        project: ResourceAllocationState['projects'][string]
    ): boolean =>
        (lens.includeDraft || project.status === ProjectStatus.Published) &&
        (lens.includeNonDisclosed ||
            project.visibility === ProjectVisibility.Disclosed) &&
        (lens.includeTentative ||
            project.certainty === ProjectCertainty.Confirmed);

    const passesLifecycle = (
        project: ResourceAllocationState['projects'][string]
    ): boolean => {
        const lifecycle = project.lifecycle ?? ProjectLifecycle.Active;
        return (
            (includeOnHold || lifecycle !== ProjectLifecycle.OnHold) &&
            (includeCancelled || lifecycle !== ProjectLifecycle.Cancelled)
        );
    };

    // Per project: keep whole, drop whole, or truncate from a week. Exclusions
    // win over everything; scenario tags readmit whole (the "revive it" view);
    // lifecycle is the only category that can truncate rather than drop.
    const droppedProjectIds = new Set<string>();
    const truncatedFrom = new Map<string, number>();
    for (const project of Object.values(state.projects)) {
        if (excludedProjectIds.has(project.id)) {
            droppedProjectIds.add(project.id);
            continue;
        }
        if (scenarioProjectIds?.has(project.id)) continue;
        // Scenario mode ignores the toggles: the base picture is the actuals
        // (published + disclosed + confirmed + active), with effective-dated
        // holds/cancellations still contributing their past.
        const nonLifecycleOk = scenarioProjectIds
            ? isActualIgnoringLifecycle(project)
            : passesNonLifecycle(project);
        const lifecycleOk = scenarioProjectIds
            ? isLifecycleActive(project)
            : passesLifecycle(project);
        if (nonLifecycleOk && lifecycleOk) continue;
        if (
            nonLifecycleOk &&
            !lifecycleOk &&
            project.lifecycleFromWeek != null
        ) {
            truncatedFrom.set(project.id, project.lifecycleFromWeek);
            continue;
        }
        droppedProjectIds.add(project.id);
    }

    // Identity: nothing to drop or truncate.
    if (droppedProjectIds.size === 0 && truncatedFrom.size === 0) {
        return state;
    }

    // Allocations go first — a truncated project keeps only pre-fromWeek weeks,
    // and everything downstream (assignments, the project row itself) follows
    // from which allocations survive.
    const allocations = omitWhere(state.allocations, al => {
        if (droppedProjectIds.has(al.projectId)) return true;
        const fromWeek = truncatedFrom.get(al.projectId);
        return fromWeek !== undefined && al.globalWeekNumber >= fromWeek;
    });

    const assignmentIdsWithAllocations = new Set(
        Object.values(allocations).map(al => al.assignmentId)
    );

    // A truncated project with NOTHING spent before the cut vanishes whole —
    // an all-future plan that was cancelled has no past to show.
    const projectIdsWithAllocations = new Set(
        Object.values(allocations).map(al => al.projectId)
    );
    for (const projectId of truncatedFrom.keys()) {
        if (!projectIdsWithAllocations.has(projectId)) {
            droppedProjectIds.add(projectId);
        }
    }

    // Truncated projects keep only assignments with surviving hours — open
    // role placeholders and future-only assignments drop with the plan.
    const droppedAssignmentIds = new Set(
        Object.values(state.assignments)
            .filter(
                a =>
                    droppedProjectIds.has(a.projectId) ||
                    (truncatedFrom.has(a.projectId) &&
                        !assignmentIdsWithAllocations.has(a.id))
            )
            .map(a => a.id)
    );

    return {
        ...state,
        projects: omitWhere(state.projects, p => droppedProjectIds.has(p.id)),
        phases: omitWhere(state.phases, ph => {
            if (droppedProjectIds.has(ph.projectId)) return true;
            const fromWeek = truncatedFrom.get(ph.projectId);
            return (
                fromWeek !== undefined &&
                ph.startGlobalWeekNumber >= fromWeek
            );
        }),
        assignments: omitWhere(state.assignments, a =>
            droppedAssignmentIds.has(a.id)
        ),
        allocations: omitWhere(allocations, al =>
            droppedAssignmentIds.has(al.assignmentId)
        ),
        projectTags: omitWhere(state.projectTags, pt =>
            droppedProjectIds.has(pt.projectId)
        ),
        projectPeople: omitWhere(state.projectPeople, pp =>
            droppedProjectIds.has(pp.projectId)
        ),
        assignmentTags: omitWhere(state.assignmentTags, at =>
            droppedAssignmentIds.has(at.assignmentId)
        ),
        allocatedAssignmentIds: state.allocatedAssignmentIds.filter(
            id => !droppedAssignmentIds.has(id)
        )
    };
}
